import { HOLDEM_BACKEND_BASE_URL, POKER_API_BASE_URL } from '../config/runtime';

export interface PokerTableState {
  tableId: string;
  tableName: string;
  guildId: string;
  maxPlayers: number;
  bigBlind: number;
  smallBlind: number;
  minBuyin: number;
  maxBuyin: number;
  loosePlay?: boolean;
  isLoosePlay?: boolean;
  players: PlayerState[];
  communityCards: string[];
  pot: number;
  currentBet: number;
  dealerSeat: number;
  activePlayerSeat: number;
  currentPhase: string;
  currentHandNumber?: number;
  hasStarted?: boolean;
  startVotes?: string[];
  startVotesRequired?: number;
  waitingForFirstHand?: boolean;
  // Prywatne karty gracza (tylko dla lokalnego gracza)
  myCards?: string[];
  mySeatNumber?: number;
  // Aliases for compatibility
  dealerPosition?: number;
  currentPlayerPosition?: number;
  phase?: string;
}

export interface HandInfo {
  handName: string;
  handDescription: string;
  handRank: number;
}

export interface ShowdownPayout {
  playerId: string;
  amount: number;
  handInfo: HandInfo | null;
}

export interface PlayerState {
  userId: string;
  username: string;
  avatar: string;
  stack: number;
  currentBet: number;
  seatNumber: number;
  cards: string[];
  isDealer: boolean;
  isBigBlind: boolean;
  isSmallBlind: boolean;
  hasActed: boolean;
  isFolded: boolean;
  isAllIn: boolean;
  isSittingOut: boolean;
  isActive: boolean;
  // Informacje o kartach (z API publicznego)
  hasCards?: boolean;
  cardCount?: number;
  // Aliases for compatibility
  chips?: number;
  bet?: number;
  position?: number;
}


export interface AuthResult {
  success: boolean;
  userId?: string;
  guildId?: string;
  psychoCoinBalance?: number;
  error?: string;
}

export interface JoinResult {
  success: boolean;
  tableId?: string;
  seatNumber?: number;
  newBalance?: number;
  error?: string;
}

export interface ActionResult {
  success: boolean;
  error?: string;
}

export interface EmojiReactionEvent {
  emoji: string;
  userId?: string;
  username?: string;
}

export interface LeaveResult {
  success: boolean;
  newBalance?: number;
  error?: string;
}

export interface TableConfig {
  name: string;
  maxPlayers?: number;
  bigBlind?: number;
  smallBlind?: number;
  minBuyin?: number;
  maxBuyin?: number;
  loosePlay?: boolean;
  isLoosePlay?: boolean;
}

export interface JoinTableOptions {
  tableId: string;
  buyin: number;
  username: string;
  avatar?: string;
  seatNumber?: number | null;
}

export class PokerClient {
  private ws: WebSocket | null = null;
  private serverUrl: string;
  private authenticated = false;
  private currentUserId: string = '';
  private currentUsername: string = '';
  private currentGuildId: string = '';
  private currentTableId: string = '';
  private currentCoinBalance: number = 0;
  private messageId = 0;
  private pendingCalls = new Map<string, { resolve: (value: any) => void, reject: (error: any) => void }>();

  // Events
  public onLog: ((msg: string) => void) | null = null;
  public onError: ((msg: string) => void) | null = null;
  public onDisconnected: (() => void) | null = null;
  public onTableCreated: ((state: PokerTableState) => void) | null = null;
  public onTableStateChanged: ((state: PokerTableState) => void) | null = null;
  public onHandStarted: ((event: any) => void) | null = null;
  public onShowdown: ((event: any) => void) | null = null;
  public onHandWinner: ((event: { playerId: string; playerName?: string; amount: number; reason: string }) => void) | null = null;
  public onPlayerAction: ((event: any) => void) | null = null;
  public onPlayerJoined: ((event: any) => void) | null = null;
  public onPlayerLeft: ((event: any) => void) | null = null;
  public onPlayerForcedSitOut: ((event: { playerId: string; playerName: string; stack: number; minimumRequired: number; tableState?: PokerTableState }) => void) | null = null;
  public onPlayerBusted: ((event: { playerId: string; playerName: string; reason?: string; stack?: number; minimumRequired?: number; tableState?: PokerTableState }) => void) | null = null;
  public onCoinBalanceChanged: ((balance: number) => void) | null = null;
  public onEmojiReceived: ((event: EmojiReactionEvent) => void) | null = null;
  public onTableLeft: ((event: { success: boolean; tableId: string }) => void) | null = null;
  public onPokerSettlementError: ((event: { manualReconciliationRequired?: boolean; retryScheduled?: boolean }) => void) | null = null;

  constructor(serverUrl?: string) {
    this.serverUrl = serverUrl || POKER_API_BASE_URL || '';
  }

  private setupHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.log('WebSocket connected');
      this.sendHandshake();
    };

    this.ws.onmessage = (event) => {
      try {
        const rawData = event.data;
        const messages = rawData.split('\x1e').filter((m: string) => m.trim());
        
        for (const msgStr of messages) {
          if (!msgStr.trim()) continue;
          try {
            const message = JSON.parse(msgStr);
            this.handleMessage(message);
          } catch (parseError) {
            this.error(`Failed to parse message: ${parseError}`);
          }
        }
      } catch (error) {
        this.error(`Message handling error: ${error}`);
      }
    };

    this.ws.onerror = (error) => {
      this.error(`WebSocket error: ${error}`);
    };

    this.ws.onclose = () => {
      this.log('WebSocket disconnected');
      this.authenticated = false;
      this.onDisconnected?.();
      this.ws = null;
    };
  }

  private sendHandshake(): void {
    if (!this.ws) return;
    
    const handshake = {
      protocol: 'json',
      version: 1
    };
    
    this.ws.send(JSON.stringify(handshake) + '\x1e');
  }

  private handleMessage(message: any): void {
    // Handle empty handshake response
    if (Object.keys(message).length === 0) {
      this.log('Handshake completed');
      return;
    }

    // Handle SignalR completion (type 3)
    if (message.type === 3 && message.invocationId) {
      const pending = this.pendingCalls.get(message.invocationId);
      if (pending) {
        this.pendingCalls.delete(message.invocationId);
        if (message.error) {
          pending.reject(new Error(message.error));
        } else {
          pending.resolve(message.result);
        }
      }
      return;
    }

    // Handle server-to-client events (SignalR invocations from server)
    if ((message.type === 1 || message.type === 'invocation') && message.target) {
      const eventData = message.arguments?.[0];
      
      switch (message.target) {
        case 'TableLeft':
          if (eventData?.tableId && eventData.tableId === this.currentTableId) {
            this.currentTableId = '';
            this.onTableLeft?.(eventData);
          }
          break;
        case 'PokerSettlementError':
          this.onPokerSettlementError?.({
            manualReconciliationRequired: eventData?.manualReconciliationRequired === true,
            retryScheduled: eventData?.retryScheduled === true,
          });
          break;
        case 'TableCreated':
          this.log('Table created');
          this.onTableCreated?.(eventData);
          break;
        case 'TableJoined':
          this.log('Table joined - received full state');
          // This event contains full table state after joining
          if (eventData?.tableState) {
            this.onTableStateChanged?.(eventData.tableState);
          }
          break;
        case 'TableStateChanged':
          this.log('Table state changed');
          this.onTableStateChanged?.(eventData);
          break;
        case 'HandStarted':
          this.log('Hand started');
          this.onHandStarted?.(eventData);
          break;
        case 'GameEvent':
          this.log('Game event: ' + JSON.stringify(eventData));
          // GameEvent ma strukturę {tableId, event: {type, ...data}}
          const gameEvent = eventData?.event || eventData;
          const eventType = gameEvent?.type;
          
          if (eventType === 'handStarted') {
            this.onHandStarted?.(gameEvent);
            // Update table state if included
            if (gameEvent?.tableState) {
              this.onTableStateChanged?.(gameEvent.tableState);
            }
          } else if (eventType === 'showdown') {
            this.onShowdown?.(gameEvent);
          } else if (eventType === 'handWinner') {
            this.onHandWinner?.(gameEvent);
          } else if (eventType === 'emojiReaction' || eventType === 'emoji') {
            this.onEmojiReceived?.(gameEvent);
          } else if (eventType === 'playerForcedSitOut') {
            this.onPlayerForcedSitOut?.(gameEvent);
            if (gameEvent?.tableState) {
              this.onTableStateChanged?.(gameEvent.tableState);
            }
          } else if (eventType === 'playerBusted') {
            this.onPlayerBusted?.(gameEvent);
            // Also refresh table state after player busted
            if (gameEvent?.tableState) {
              this.onTableStateChanged?.(gameEvent.tableState);
            }
          } else if (eventType === 'phaseChanged') {
            // Phase change event - update table state
            if (gameEvent?.tableState) {
              this.onTableStateChanged?.(gameEvent.tableState);
            }
          } else if (eventType === 'playerAction') {
            // Player action from game event
            this.onPlayerAction?.(gameEvent);
            if (gameEvent?.tableState) {
              this.onTableStateChanged?.(gameEvent.tableState);
            }
          } else if (gameEvent?.tableState) {
            this.onTableStateChanged?.(gameEvent.tableState);
          }
          break;
        case 'Showdown':
          this.log('Showdown');
          this.onShowdown?.(eventData);
          break;
        case 'PlayerAction':
          this.log('Player action');
          this.onPlayerAction?.(eventData);
          break;
        case 'PlayerJoined':
          this.log('Player joined');
          this.onPlayerJoined?.(eventData);
          // Also update table state if included
          if (eventData?.tableState) {
            this.onTableStateChanged?.(eventData.tableState);
          }
          break;
        case 'PlayerLeft':
          this.log('Player left');
          this.onPlayerLeft?.(eventData);
          break;
        case 'EmojiReaction':
          this.log('Emoji reaction');
          this.onEmojiReceived?.(eventData);
          break;
        case 'PlayerSitIn':
        case 'PlayerSitOut':
          this.log('Player sit state changed');
          if (eventData?.tableState) {
            this.onTableStateChanged?.(eventData.tableState);
          }
          break;
        default:
          this.log(`Unhandled event: ${message.target}`);
          console.log('Event data:', eventData);
      }
    }
  }

  private async invokeMethod<T>(method: string, ...args: any[]): Promise<T> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }

    const invocationId = `${++this.messageId}`;
    
    return new Promise((resolve, reject) => {
      this.pendingCalls.set(invocationId, { resolve, reject });

      const message = {
        type: 1,
        invocationId,
        target: method,
        arguments: args,
      };

      this.ws!.send(JSON.stringify(message) + '\x1e');

      setTimeout(() => {
        if (this.pendingCalls.has(invocationId)) {
          this.pendingCalls.delete(invocationId);
          reject(new Error(`Method ${method} timed out`));
        }
      }, 30000);
    });
  }

  public async connect(): Promise<boolean> {
    try {
      const wsUrl = this.serverUrl.replace(/^http/, 'ws') + '/poker';
      
      this.ws = new WebSocket(wsUrl);
      this.setupHandlers();

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Connection timeout'));
        }, 10000);

        this.ws!.onopen = () => {
          clearTimeout(timeout);
          this.log('Connected to server');
          this.sendHandshake();
          resolve();
        };

        this.ws!.onerror = (error) => {
          clearTimeout(timeout);
          reject(error);
        };
      });

      this.setupHandlers();
      
      return true;
    } catch (error) {
      this.error(`Connection failed: ${error}`);
      return false;
    }
  }

  public async authenticate(): Promise<AuthResult> {
    try {
      const response = await fetch(`${HOLDEM_BACKEND_BASE_URL}/api/ws-token`, { credentials: 'include' });
      if (!response.ok) return { success: false, error: 'Authentication required' };
      const { token } = await response.json();
      if (typeof token !== 'string' || !token) return { success: false, error: 'Authentication required' };

      const result = await this.invokeMethod<AuthResult>('Authenticate', { token });
      
      if (result.success) {
        if (!result.userId || !result.guildId) {
          return { success: false, error: 'Invalid authentication response' };
        }
        this.authenticated = true;
        this.currentUserId = result.userId;
        this.currentGuildId = result.guildId;
        if (result.psychoCoinBalance !== undefined) {
          this.currentCoinBalance = result.psychoCoinBalance;
          this.onCoinBalanceChanged?.(result.psychoCoinBalance);
        }
        this.log(`Authenticated as ${result.userId} (Balance: ${this.currentCoinBalance} 💰)`);
      } else {
        this.error(`Authentication failed: ${result.error}`);
      }
      
      return result;
    } catch (error) {
      this.error(`Authentication error: ${error}`);
      return { success: false, error: String(error) };
    }
  }

  public async getTables(guildId: string): Promise<PokerTableState[] | null> {
    try {
      const tables = await this.invokeMethod<PokerTableState[]>('GetTables', guildId);
      return tables;
    } catch (error) {
      this.error(`Get tables failed: ${error}`);
      return null;
    }
  }

  public async createTable(guildId: string, config: TableConfig): Promise<PokerTableState | null> {
    try {
      const loosePlay = config.loosePlay ?? config.isLoosePlay;
      // Map 'name' to 'tableName' for backend compatibility
      const backendConfig = {
        tableName: config.name,
        maxPlayers: config.maxPlayers,
        bigBlind: config.bigBlind,
        smallBlind: config.smallBlind,
        minBuyin: config.minBuyin,
        maxBuyin: config.maxBuyin,
        loosePlay: loosePlay,
        isLoosePlay: loosePlay,
      };
      const table = await this.invokeMethod<PokerTableState>('CreateTable', guildId, backendConfig);
      return table;
    } catch (error) {
      this.error(`Create table failed: ${error}`);
      return null;
    }
  }

  public async joinTable(options: JoinTableOptions): Promise<JoinResult> {
    try {
      const result = await this.invokeMethod<JoinResult>('JoinTable', options);
      if (result.success && result.tableId) {
        this.currentTableId = result.tableId;
        if (typeof result.newBalance === 'number') {
          this.setCoinBalance(result.newBalance);
        }
      }
      return result;
    } catch (error) {
      this.error(`Join table failed: ${error}`);
      return { success: false, error: String(error) };
    }
  }

  public async leaveTable(tableId?: string): Promise<LeaveResult> {
    try {
      const targetTableId = tableId || this.currentTableId;
      if (!targetTableId) {
        return { success: true }; // Not at any table
      }
      const result = await this.invokeMethod<LeaveResult>('LeaveTable', targetTableId);
      if (result.success) {
        this.currentTableId = '';
        if (typeof result.newBalance === 'number') {
          this.setCoinBalance(result.newBalance);
        }
      }
      return result;
    } catch (error) {
      this.error(`Leave table failed: ${error}`);
      return { success: false, error: String(error) };
    }
  }

  public async getTableState(tableId: string): Promise<PokerTableState | null> {
    try {
      const state = await this.invokeMethod<PokerTableState>('GetTableState', tableId);
      return state;
    } catch (error) {
      this.error(`Get table state failed: ${error}`);
      return null;
    }
  }

  /**
   * Wykonaj akcję gracza - wszystkie akcje idą przez PlayerAction
   */
  private async playerAction(action: string, amount?: number): Promise<ActionResult> {
    try {
      const actionData = { action, amount };
      this.log(`Sending PlayerAction: ${JSON.stringify(actionData)}`);
      return await this.invokeMethod<ActionResult>('PlayerAction', actionData);
    } catch (error) {
      this.error(`Player action failed: ${error}`);
      return { success: false, error: String(error) };
    }
  }

  public async fold(): Promise<ActionResult> {
    return this.playerAction('fold');
  }

  public async check(): Promise<ActionResult> {
    return this.playerAction('check');
  }

  public async call(): Promise<ActionResult> {
    return this.playerAction('call');
  }

  public async raise(amount: number): Promise<ActionResult> {
    return this.playerAction('raise', amount);
  }

  public async allIn(): Promise<ActionResult> {
    return this.playerAction('all_in');
  }

  public async sitOut(): Promise<ActionResult> {
    try {
      return await this.invokeMethod<ActionResult>('SitOut');
    } catch (error) {
      return { success: false, error: String(error) };
    }
  }

  public async sitIn(): Promise<ActionResult> {
    try {
      return await this.invokeMethod<ActionResult>('SitIn');
    } catch (error) {
      return { success: false, error: String(error) };
    }
  }

  public async requestStart(): Promise<ActionResult> {
    try {
      return await this.invokeMethod<ActionResult>('RequestStart');
    } catch (error) {
      this.error(`Start vote failed: ${error}`);
      return { success: false, error: String(error) };
    }
  }

  public async sendEmojiReaction(emoji: string, username?: string, userId?: string): Promise<ActionResult> {
    try {
      const payload: EmojiReactionEvent = { emoji };
      if (username) {
        payload.username = username;
      }
      if (userId) {
        payload.userId = userId;
      }
      return await this.invokeMethod<ActionResult>('SendEmojiReaction', payload);
    } catch (error) {
      this.error(`Send emoji reaction failed: ${error}`);
      return { success: false, error: String(error) };
    }
  }

  public async disconnect(): Promise<void> {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  public get isAuthenticated(): boolean {
    return this.authenticated;
  }

  public get userId(): string {
    return this.currentUserId;
  }

  public get username(): string {
    return this.currentUsername;
  }

  public setUsername(username: string): void {
    this.currentUsername = username;
  }

  public get guildId(): string {
    return this.currentGuildId;
  }

  public get tableId(): string {
    return this.currentTableId;
  }

  public get coinBalance(): number {
    return this.currentCoinBalance;
  }

  public setCoinBalance(balance: number): void {
    this.currentCoinBalance = balance;
    this.onCoinBalanceChanged?.(balance);
  }

  private log(message: string): void {
    // console.log(`[PokerClient] ${message}`);
    this.onLog?.(message);
  }

  private error(message: string): void {
    console.error(`[PokerClient] ${message}`);
    this.onError?.(message);
  }
}
